import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Swords, Camera, MousePointer, Link2, ShoppingBag, Tag,
  ChevronRight, Upload, Check, Clock, X, ExternalLink, Image
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const QUEST_TYPE_ICONS = {
  screenshot: Camera,
  manual: MousePointer,
  link_visit: Link2,
  purchase: ShoppingBag,
};

const QUEST_TYPE_COLORS = {
  screenshot: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  manual: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  link_visit: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  purchase: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
};

export default function QuestsPage() {
  const { user, token, API, refreshUser } = useAuth();
  const [quests, setQuests] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuest, setActiveQuest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [tab, setTab] = useState('available');
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [questsRes, subsRes] = await Promise.all([
        axios.get(`${API}/quests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/quests/my-submissions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setQuests(questsRes.data);
      setSubmissions(subsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLinkVisit = async (quest) => {
    if (quest.link_url) {
      window.open(quest.link_url, '_blank');
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/quests/${quest.id}/submit`, { notes: 'Link visited' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  const handleImageSubmit = async (quest, file, notes = '') => {
    setSubmitting(true);
    setUploadProgress('Uploading image...');
    try {
      // Upload image
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const imagePath = uploadRes.data.path;

      setUploadProgress(quest.quest_type === 'purchase' ? 'Analyzing receipt...' : 'Submitting...');

      // Submit quest with image path
      await axios.post(`${API}/quests/${quest.id}/submit`, {
        proof_image_path: imagePath,
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUploadProgress(null);
      setActiveQuest(null);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed');
    }
    setUploadProgress(null);
    setSubmitting(false);
  };

  const handleManualSubmit = async (quest, notes) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/quests/${quest.id}/submit`, { notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveQuest(null);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    }
    setSubmitting(false);
  };

  const availableQuests = quests.filter(q => q.can_submit);
  const completedQuests = quests.filter(q => !q.can_submit);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4" data-testid="quests-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-red-500/20 border border-amber-500/20 flex items-center justify-center">
          <Swords className="w-4.5 h-4.5 text-amber-500" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-base text-white">Quests</h2>
          <p className="text-[10px] text-zinc-500">{availableQuests.length} available</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.03] rounded-xl p-1">
        {[
          { id: 'available', label: `Available (${availableQuests.length})` },
          { id: 'submissions', label: `My Submissions (${submissions.length})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-medium transition-all ${
              tab === t.id ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`quest-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.15 }}
        >
          {tab === 'available' && (
            <div className="space-y-2">
              {availableQuests.length === 0 && (
                <div className="text-center py-10 glass-card rounded-xl">
                  <Swords className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500">No quests available right now</p>
                  <p className="text-[10px] text-zinc-600 mt-1">Check back later for new quests!</p>
                </div>
              )}
              {availableQuests.map((quest, i) => (
                <QuestCard key={quest.id} quest={quest} index={i} onAction={(q) => {
                  if (q.quest_type === 'link_visit') {
                    handleLinkVisit(q);
                  } else {
                    setActiveQuest(q);
                  }
                }} submitting={submitting} />
              ))}
              {completedQuests.length > 0 && (
                <>
                  <p className="text-xs font-bold text-zinc-500 pt-2">Completed</p>
                  {completedQuests.map((quest, i) => (
                    <QuestCard key={quest.id} quest={quest} index={i} completed />
                  ))}
                </>
              )}
            </div>
          )}

          {tab === 'submissions' && (
            <div className="space-y-2">
              {submissions.length === 0 && (
                <div className="text-center py-10 glass-card rounded-xl">
                  <Clock className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500">No submissions yet</p>
                </div>
              )}
              {submissions.map((sub, i) => (
                <motion.div
                  key={sub.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="glass-card rounded-xl p-3"
                  data-testid={`submission-${i}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold text-white">{sub.quest_title}</p>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full border ${
                      sub.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                      sub.status === 'rejected' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                      'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }`}>
                      {sub.status === 'approved' ? 'Approved' : sub.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                    <span>{new Date(sub.submitted_at).toLocaleDateString()}</span>
                    {sub.status === 'approved' && <span className="text-amber-500 font-mono">+{sub.quest_points} pts</span>}
                    {sub.proof_image_path && <span className="flex items-center gap-1"><Image className="w-3 h-3" />Image attached</span>}
                  </div>
                  {sub.admin_notes && (
                    <p className="text-[9px] text-zinc-600 mt-1 italic">Note: {sub.admin_notes}</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Submit Quest Dialog */}
      <Dialog open={!!activeQuest} onOpenChange={(v) => { if (!v && !submitting) setActiveQuest(null); }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-base">{activeQuest?.title}</DialogTitle>
          </DialogHeader>
          {activeQuest && (
            <QuestSubmitForm
              quest={activeQuest}
              submitting={submitting}
              uploadProgress={uploadProgress}
              onImageSubmit={handleImageSubmit}
              onManualSubmit={handleManualSubmit}
              fileInputRef={fileInputRef}
            />
          )}
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
      />
    </div>
  );
}

function QuestCard({ quest, index, onAction, completed, submitting }) {
  const colors = QUEST_TYPE_COLORS[quest.quest_type] || QUEST_TYPE_COLORS.manual;
  const TypeIcon = QUEST_TYPE_ICONS[quest.quest_type] || Swords;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`glass-card rounded-xl p-3.5 ${completed ? 'opacity-60' : ''}`}
      data-testid={`quest-card-${index}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${quest.thumbnail_url ? '' : colors.bg} border ${quest.thumbnail_url ? 'border-white/10' : colors.border} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
          {quest.thumbnail_url ? (
            <img src={quest.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <TypeIcon className={`w-5 h-5 ${colors.text}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-xs font-bold text-white">{quest.title}</h3>
            <span className="text-xs font-mono font-bold text-amber-500">+{quest.points}</span>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed">{quest.description}</p>
          <div className="flex items-center gap-3 mt-2 text-[9px] text-zinc-600">
            {quest.attached_code && (
              <span className="flex items-center gap-1 text-purple-400">
                <Tag className="w-3 h-3" />
                {quest.attached_code}
              </span>
            )}
            <span>{quest.per_user_limit}x limit</span>
            {quest.user_pending > 0 && <span className="text-amber-400">Pending review</span>}
          </div>
        </div>
        {!completed && onAction && (
          <button
            onClick={() => onAction(quest)}
            disabled={submitting}
            className={`px-3 py-2 rounded-lg text-[10px] font-bold flex items-center gap-1.5 flex-shrink-0 transition-all ${
              quest.quest_type === 'link_visit'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                : 'honey-btn'
            } disabled:opacity-50`}
            data-testid={`quest-action-${index}`}
          >
            {quest.quest_type === 'link_visit' ? (
              <>
                <ExternalLink className="w-3 h-3" />
                Visit
              </>
            ) : (
              <>
                <ChevronRight className="w-3 h-3" />
                Start
              </>
            )}
          </button>
        )}
        {completed && (
          <div className="flex items-center gap-1 text-emerald-400 text-[10px]">
            <Check className="w-3.5 h-3.5" />
            Done
          </div>
        )}
      </div>
    </motion.div>
  );
}

function QuestSubmitForm({ quest, submitting, uploadProgress, onImageSubmit, onManualSubmit, fileInputRef }) {
  const [notes, setNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const needsImage = quest.quest_type === 'screenshot' || quest.quest_type === 'purchase';

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (needsImage && selectedFile) {
      onImageSubmit(quest, selectedFile, notes);
    } else if (quest.quest_type === 'manual') {
      onManualSubmit(quest, notes);
    }
  };

  return (
    <div className="space-y-4 py-1">
      <p className="text-xs text-zinc-400">{quest.description}</p>

      {quest.attached_code && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
          <Tag className="w-4 h-4 text-purple-400" />
          <span className="text-xs text-purple-400">Use code: <span className="font-mono font-bold">{quest.attached_code}</span></span>
        </div>
      )}

      {needsImage && (
        <div>
          <Label className="text-xs text-zinc-500 mb-2 block">
            {quest.quest_type === 'purchase' ? 'Upload Receipt' : 'Upload Screenshot'}
          </Label>
          {preview ? (
            <div className="relative">
              <img src={preview} alt="Preview" className="w-full rounded-lg max-h-48 object-contain bg-zinc-900" />
              <button
                onClick={() => { setSelectedFile(null); setPreview(null); }}
                className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/jpeg,image/png,image/webp';
                input.onchange = handleFileSelect;
                input.click();
              }}
              className="w-full py-8 rounded-xl border-2 border-dashed border-white/10 hover:border-amber-500/30 flex flex-col items-center gap-2 transition-colors"
              data-testid="quest-upload-btn"
            >
              <Upload className="w-6 h-6 text-zinc-600" />
              <span className="text-xs text-zinc-500">Click to upload image</span>
              <span className="text-[9px] text-zinc-700">JPEG, PNG, WEBP (max 10MB)</span>
            </button>
          )}
        </div>
      )}

      {quest.quest_type === 'manual' && (
        <div>
          <Label className="text-xs text-zinc-500">Notes (optional)</Label>
          <Input
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any details about completion..."
            className="bg-[#09090B] border-white/10 text-white mt-1"
          />
        </div>
      )}

      {uploadProgress && (
        <div className="flex items-center gap-2 text-xs text-amber-400">
          <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          {uploadProgress}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={submitting || (needsImage && !selectedFile)}
          className="flex-1 honey-btn px-4 py-2.5 rounded-xl text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="quest-submit-btn"
        >
          {submitting ? (
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4" />
              Submit
            </>
          )}
        </button>
      </div>
    </div>
  );
}
